import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import AsyncSelect from "react-select/async";
import AsyncCreatable from "react-select/async-creatable";
import { TreeSelect } from "antd";
import { InputTextarea } from "primereact/inputtextarea";
import { SelectButton } from "primereact/selectbutton";
import { QueryBuilder, formatQuery } from "react-querybuilder";
import Select from "../../inputs/select";
import Radio from "../../inputs/radio";
import Basic from "../../inputs/basic";
import { QueryBuilderValueEditor, QueryBuilderSelectField, QueryBuilderCombinatorSelector, QueryBuilderButton, QueryBuilderCloseButton } from "../../inputs/querybuilder";
import { getCategories, getGARMTaxonomy, getSentimentTaxonomy } from "../../../services/cohort";
import { getKeywordsByTag, getTaxonomyByTag } from "../../../services/tag";
import CriterionSummary from "./CriterionSummary";
import * as CONST from "../../../utilities/constants";
import eyeIcon from "../../../assets/svgs/eyeopen.svg";
import "./ContentConsumptionCard.scss";

/**
 * ContentConsumptionCard - Simplified criteria card for behavioral site content targeting
 *
 * Data format: {
 *   action: "include" | "exclude",
 *   criteria: {
 *     type: "categories" | "keywords" | "urls" | "garm" | "sentiments" | "default" | "all",
 *     categories: [...],
 *     keywords: [...],
 *     urls: [...],
 *     garm: [...],
 *     sentiments: [...],
 *     taxonomy: {...},
 *   },
 *   modifier: {
 *     type: "pageViews" | "timeSpent" | "adClicks",
 *     count: number,
 *     lookback: number,
 *     timeUnit: "minutes" | "seconds"  // only for timeSpent
 *   }
 * }
 */

// Criteria type options for behavioral targeting
const CRITERIA_OPTIONS = [
	{ value: "all", label: "All Site Content", info: "Target across all content on your site" },
	{ value: "categories", label: "IAB Categories", info: "Target sites falling under specific content categories" },
	{ value: "keywords", label: "Keywords", info: "Target keywords or topics that match the content on your site" },
	{ value: "urls", label: "URLs", info: "Target specific sites" },
	{ value: "default", label: "Custom Content Taxonomy", info: "Target sites falling under specific criteria", isTaxonomy: true },
	{ value: "garm", label: "Brand Safety + Suitability", info: "Target sites by brand safety risk level" },
	{ value: "sentiments", label: "Sentiment Targeting", info: "Target sites with specific sentiments" },
];

// Criteria filter options for inline dropdown (with None option for optional filtering)
const CRITERIA_FILTER_OPTIONS = [
	{ value: null, label: "None", info: "No additional filtering" },
	{ value: "categories", label: "IAB Categories", info: "Target sites falling under specific content categories" },
	{ value: "keywords", label: "Keywords", info: "Target keywords or topics that match the content on your site" },
	{ value: "urls", label: "URLs", info: "Target specific sites" },
	{ value: "default", label: "Custom Content Taxonomy", info: "Target sites falling under specific criteria", isTaxonomy: true },
	{ value: "garm", label: "Brand Safety + Suitability", info: "Target sites by brand safety risk level" },
	{ value: "sentiments", label: "Sentiment Targeting", info: "Target sites with specific sentiments" },
];

// Filter criteria options based on customTaxonomies settings
const filterCriteriaOptions = (options, customTaxonomies = []) => {
	return options
		.filter((opt) => !opt.isTaxonomy || customTaxonomies.includes("default"))
		.filter((opt) => opt.value !== "garm" || customTaxonomies.includes("garm"))
		.filter((opt) => opt.value !== "sentiments" || customTaxonomies.includes("sentiments"));
};

function ContentConsumptionCard({ data, onChange, isReadOnly, isCollapsed, tagId, modifierConfiguration = [], modifierDefaults = {}, customTaxonomies = [], cardId, isModifierPartiallyEditable = false }) {

	// Filter criteria options based on customTaxonomies
	const filteredCriteriaOptions = filterCriteriaOptions(CRITERIA_OPTIONS, customTaxonomies);
	const filteredCriteriaFilterOptions = filterCriteriaOptions(CRITERIA_FILTER_OPTIONS, customTaxonomies);

	// Get current action
	const getCurrentAction = () => {
		return CONST.ruleActions.find((a) => a.value === data?.action) || CONST.ruleActions[0];
	};

	// Get current criteria filter for the inline dropdown (uses filtered CRITERIA_FILTER_OPTIONS)
	const getCurrentCriteriaFilter = () => {
		const criteriaType = data?.criteria?.type;
		if (!criteriaType || criteriaType === "all") {
			return filteredCriteriaFilterOptions[0]; // None
		}
		return filteredCriteriaFilterOptions.find((opt) => opt.value === criteriaType) || filteredCriteriaFilterOptions[0];
	};

	// Handle action change
	const handleActionChange = (action) => {
		onChange({
			...data,
			action: action.value,
		});
	};

	// Handle criteria type change (handles both regular and filter dropdown)
	const handleCriteriaTypeChange = (option) => {
		// Handle None selection (null value) - reset criteria
		if (!option || option.value === null) {
			onChange({
				...data,
				criteria: { type: null },
			});
		} else {
			onChange({
				...data,
				criteria: {
					type: option.value,
				},
			});
		}
	};

	// Handle criteria data change (from sub-components)
	const handleCriteriaDataChange = (criteriaData) => {
		onChange({
			...data,
			criteria: {
				...data?.criteria,
				...criteriaData,
			},
		});
	};

	// Handle modifier change
	const handleModifierChange = (modifierData) => {
		const newModifierType = modifierData.type ?? data?.modifier?.type;
		// Reset criteria to "all" when switching to Ad Clicks or custom modifier
		const shouldResetCriteria = newModifierType &&
			(newModifierType === "adClicks" || newModifierType.startsWith("custom|"));

		onChange({
			...data,
			modifier: {
				...data?.modifier,
				...modifierData,
			},
			// Reset criteria when switching to Ad Clicks or custom modifier
			...(shouldResetCriteria && { criteria: { type: "all" } }),
		});
	};

	// Check if current modifier should show criteria selection
	// Returns true for Page Views and Time Spent, false for Ad Clicks and custom modifiers
	const shouldShowCriteria = () => {
		const modifierType = data?.modifier?.type;
		if (!modifierType) return false;
		// Hide criteria for custom modifiers and Ad Clicks
		if (modifierType.startsWith("custom|") || modifierType === "adClicks") return false;
		return true;
	};

	// Collapsed view
	if (isCollapsed) {
		return (
			<div className="content-consumption-card collapsed">
				<CriterionSummary
					type="contentConsumption"
					data={data}
					modifierConfiguration={modifierConfiguration}
				/>
			</div>
		);
	}

	// Expanded view
	return (
		<div className="content-consumption-card expanded">
			{/* Include/Exclude action */}
			<div className="rule-action">
				<Radio
					options={CONST.ruleActions}
					name={`content-consumption-action-${cardId}`}
					style="list"
					disabled={isReadOnly}
					required={false}
					selected={getCurrentAction()}
					onInput={handleActionChange}
				/>
			</div>

			{/* Engagement Modifier - required first, with inline criteria filter */}
			<EngagementModifier
				data={data?.modifier}
				onChange={handleModifierChange}
				isReadOnly={isReadOnly}
				required={true}
				modifierConfiguration={modifierConfiguration}
				modifierDefaults={modifierDefaults}
				// Props for inline criteria filter
				criteriaType={data?.criteria?.type}
				criteriaFilter={getCurrentCriteriaFilter()}
				criteriaOptions={filteredCriteriaFilterOptions}
				onCriteriaChange={handleCriteriaTypeChange}
				isModifierPartiallyEditable={isModifierPartiallyEditable}
			/>

			{/* Show criteria content when a filter is selected (not None/null) */}
			{shouldShowCriteria() && data?.criteria?.type && data?.criteria?.type !== "all" && (
				<CriteriaContent
					criteriaType={data?.criteria?.type}
					data={data?.criteria}
					onChange={handleCriteriaDataChange}
					isReadOnly={isReadOnly}
					tagId={tagId}
				/>
			)}
		</div>
	);
}

// Sub-component for criteria-specific content (same as RealtimeSiteContentCard)
function CriteriaContent({ criteriaType, data, onChange, isReadOnly, tagId }) {
	switch (criteriaType) {
		case "categories":
			return <CategoriesSelector data={data} onChange={onChange} isReadOnly={isReadOnly} />;
		case "keywords":
			return <KeywordsSelector data={data} onChange={onChange} isReadOnly={isReadOnly} tagId={tagId} />;
		case "urls":
			return <UrlsSelector data={data} onChange={onChange} isReadOnly={isReadOnly} />;
		case "garm":
			return <GARMSelector data={data} onChange={onChange} isReadOnly={isReadOnly} />;
		case "sentiments":
			return <SentimentsSelector data={data} onChange={onChange} isReadOnly={isReadOnly} />;
		case "default":
			return <TaxonomySelector data={data} onChange={onChange} isReadOnly={isReadOnly} tagId={tagId} />;
		case "all":
		default:
			return null;
	}
}

CriteriaContent.propTypes = {
	criteriaType: PropTypes.string,
	data: PropTypes.object,
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
	tagId: PropTypes.string,
};

// Engagement Modifier component
function EngagementModifier({
	data,
	onChange,
	isReadOnly,
	required,
	modifierConfiguration = [],
	modifierDefaults = {},
	// Inline criteria filter props
	criteriaType,
	criteriaFilter,
	criteriaOptions = [],
	onCriteriaChange,
	isModifierPartiallyEditable = false,
}) {
	// Resolve the default lookback (days) for a modifier from the per-publisher config,
	// keyed by the modifier's option value ("pageViews" | "timeSpent" | "adClicks" | `custom|${event}`),
	// falling back to 30 (1 month) when unconfigured.
	const resolveDefaultLookback = (modifierKey) => modifierDefaults?.[modifierKey]?.lookback ?? 30;
	const defaultTimeUnit = CONST.timeUnits[0];
	const initialQuery = { combinator: "or", rules: [] };
	const [query, setQuery] = useState(data?.filterBy || initialQuery);

	// Package query for storage (convert types based on filter definitions)
	const packageQuery = (q, filters) => {
		const newQuery = JSON.parse(formatQuery(q, { format: "json_without_ids" }));
		const typeMap = filters.reduce((acc, item) => {
			acc[item.name] = item.inputType;
			return acc;
		}, {});
		newQuery.rules.forEach((rule) => {
			const fieldType = typeMap[rule.field];
			if (rule.value !== "") {
				switch (fieldType) {
					case "number":
						const numberValue = Number(rule.value);
						if (!isNaN(numberValue)) {
							rule.value = numberValue;
						}
						break;
					case "boolean":
						if (typeof rule.value === "string") {
							rule.value = rule.value.toLowerCase() === "true";
						} else {
							rule.value = Boolean(rule.value);
						}
						break;
				}
			}
		});
		return newQuery;
	};

	// Build options list: all standard modifiers plus custom modifiers for "all" target type
	const getModifierOptions = () => {
		// Standard modifiers (Page Views, Time Spent). ASB-3720: the deprecated "Ad Clicks" is only
		// offered when this cohort already uses it, so existing audiences still render and re-save.
		const baseOptions = [
			...CONST.engagementOptions,
			...CONST.deprecatedEngagementOptions.filter((opt) => opt.value === data?.type),
		];

		// Add custom modifiers that support "all" target type (since modifier is selected first)
		const customOptions = modifierConfiguration
			.filter((el) => (el.targetTypes || []).includes("all"))
			.map((el) => ({
				value: `custom|${el.event}`,
				label: `${el.publisherName ?? "Custom"}: ${el.label}`,
				line: el.actionLabel,
				line2: el.actionQuantityLabel,
				isCustom: true,
				filters: (el.filters || []).map((f) => {
					const filter = {
						name: f.field,
						label: f.label,
						operators: CONST.queryBuilderOperators.filter((op) => f.operators.includes(op.name)),
						customDataType: "custom_modifier",
						autocomplete: false,
						eventName: el.event,
						valueMap: f.valueMap ?? [],
						tagId: el.tagId,
					};
					switch (f.type) {
						case "integer":
							filter.inputType = "number";
							break;
						case "boolean":
							filter.inputType = "boolean";
							filter.defaultValue = true;
							filter.valueEditorType = "checkbox";
							break;
						case "date":
							filter.inputType = "date";
							filter.operators = CONST.queryBuilderDateOperators;
							break;
						case "string":
							filter.inputType = "string";
							filter.autocomplete = true;
							filter.multiValue = true;
							break;
					}
					return filter;
				}),
			}));

		return [...baseOptions, ...customOptions];
	};

	const modifierOptions = getModifierOptions();

	const getSelectedType = () => {
		if (!data?.type) return null;
		return modifierOptions.find((opt) => opt.value === data.type) || null;
	};

	const getSelectedLookback = () => {
		const lookback = data?.lookback || resolveDefaultLookback(data?.type);
		return CONST.ttlOptions.find((opt) => opt.value === lookback) || { value: lookback, label: `${lookback} days` };
	};

	const getSelectedTimeUnit = () => {
		if (!data?.timeUnit) return defaultTimeUnit;
		return CONST.timeUnits.find((opt) => opt.value === data.timeUnit) || defaultTimeUnit;
	};

	const handleTypeChange = (option) => {
		if (!option) {
			setQuery(initialQuery);
			onChange({ type: null, count: null, lookback: null, timeUnit: null, filterBy: null });
		} else {
			setQuery(initialQuery);
			onChange({
				type: option.value,
				count: data?.count || null,
				lookback: data?.lookback || resolveDefaultLookback(option.value),
				timeUnit: option.value === "timeSpent" ? (data?.timeUnit || "minutes") : null,
				filterBy: null,
			});
		}
	};

	const handleQueryChange = (newQuery) => {
		setQuery(newQuery);
		const selectedType = getSelectedType();
		if (selectedType?.isCustom && selectedType?.filters?.length) {
			onChange({ filterBy: packageQuery(newQuery, selectedType.filters) });
		}
	};

	const handleCountChange = (e) => {
		onChange({ count: parseInt(e.target.value, 10) || null });
	};

	const handleLookbackChange = (option) => {
		onChange({ lookback: option.value });
	};

	const handleTimeUnitChange = (option) => {
		onChange({ timeUnit: option.value });
	};

	const selectedType = getSelectedType();

	// Check if this is a standard modifier that should show criteria filter
	const isStandardModifier = selectedType && !selectedType.isCustom && selectedType.value !== "adClicks";

	// Check if a criteria filter is selected (not None/null)
	const hasCriteriaSelected = criteriaType && criteriaType !== "all" && criteriaType !== null;

	return (
		<div className="engagement-modifier">
			<Select
				label="Select Engagement Modifier"
				name="engagement-modifier"
				options={modifierOptions}
				selected={selectedType}
				onSubmit={handleTypeChange}
				disabled={isReadOnly}
				required={required}
				search={false}
				clear={true}
				clearCallback={() => handleTypeChange(null)}
			/>

			{selectedType && (
				<div className="modifier-fields">
					<span className="modifier-text">{selectedType.line}</span>
					<Basic
						name="engagement-count"
						required={true}
						value={data?.count || ""}
						disabled={isReadOnly && !isModifierPartiallyEditable}
						customClasses="count-input"
						onInput={handleCountChange}
					/>
					{selectedType.value === "timeSpent" && (
						<Select
							name="time-unit"
							options={CONST.timeUnits}
							selected={getSelectedTimeUnit()}
							onSubmit={handleTimeUnitChange}
							disabled={isReadOnly && !isModifierPartiallyEditable}
							required={true}
							search={false}
							width="short"
						/>
					)}
					<span className="modifier-text">{selectedType.line2}</span>
					<Select
						name="engagement-lookback"
						options={CONST.ttlOptions}
						selected={getSelectedLookback()}
						onSubmit={handleLookbackChange}
						disabled={isReadOnly && !isModifierPartiallyEditable}
						required={true}
						search={true}
						width="medium"
					/>
					{/* "where" shown inline when a criteria filter is selected */}
					{isStandardModifier && hasCriteriaSelected && (
						<span className="modifier-text">where</span>
					)}
					{/* Custom modifier "where" for query rules */}
					{selectedType.isCustom && query?.rules?.length > 0 && (
						<span className="modifier-text">where</span>
					)}
				</div>
			)}

			{/* Filter dropdown for standard modifiers - shown after modifier fields */}
			{isStandardModifier && (
				<Select
					label="Filter by Targeting Criteria"
					name="criteria-filter"
					options={criteriaOptions}
					selected={criteriaFilter}
					onSubmit={onCriteriaChange}
					disabled={isReadOnly}
					required={false}
					search={false}
					info={criteriaFilter?.info}
				/>
			)}
			{selectedType?.isCustom && selectedType?.filters?.length > 0 && (
				<div className={query?.rules?.length > 0 ? "one-or-more-rules" : ""}>
					<QueryBuilder
						fields={selectedType.filters}
						query={query}
						onQueryChange={handleQueryChange}
						listsAsArrays
						translations={CONST.queryBuilderTranslations}
						combinators={CONST.queryBuilderCombinators}
						operators={CONST.queryBuilderOperators}
						showCombinatorsBetweenRules
						controlElements={{
							addGroupAction: () => null,
							valueSelector: QueryBuilderSelectField,
							valueEditor: QueryBuilderValueEditor,
							addRuleAction: QueryBuilderButton,
							removeRuleAction: QueryBuilderCloseButton,
							combinatorSelector: QueryBuilderCombinatorSelector,
						}}
					/>
				</div>
			)}
		</div>
	);
}

EngagementModifier.propTypes = {
	data: PropTypes.shape({
		type: PropTypes.string,
		count: PropTypes.number,
		lookback: PropTypes.number,
		timeUnit: PropTypes.string,
		filterBy: PropTypes.object,
	}),
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
	required: PropTypes.bool,
	modifierConfiguration: PropTypes.array,
	modifierDefaults: PropTypes.object,
	// Inline criteria filter props
	criteriaType: PropTypes.string,
	criteriaFilter: PropTypes.object,
	criteriaOptions: PropTypes.array,
	onCriteriaChange: PropTypes.func,
	isModifierPartiallyEditable: PropTypes.bool,
};

// Categories selector using TreeSelect (same as SyndicatedCard and RealtimeSiteContentCard)
function CategoriesSelector({ data, onChange, isReadOnly }) {
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(true);

	// Build a set of parent category IDs from loaded tree data
	const parentIds = new Set(options.map((parent) => parent.value.split("|")[0]));

	// Transform selected data to TreeSelect format with labels (using labelInValue)
	const getSelectedValues = () => {
		if (!data?.categories) return [];
		return data.categories.map((cat) => {
			if (typeof cat === "string") {
				const [id, text] = cat.split("|");
				return { value: `${id}|${text}|false`, label: text };
			}
			const isParent = cat.isParent || parentIds.has(String(cat.id));
			const text = cat.text || String(cat.id);
			return { value: `${cat.id}|${text}|${isParent}`, label: isParent ? `All ${text}` : text };
		});
	};

	useEffect(() => {
		const loadCategories = async () => {
			try {
				setLoading(true);
				const categories = await getCategories();
				// Transform to TreeSelect format
				const treeData = categories.map((parent) => ({
					value: `${parent.id}|${parent.text}|true`,
					title: parent.text,
					children: parent.items
						.map((child) => ({
							value: `${child.id}|${child.text}|false`,
							title: child.text,
						}))
						.sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase())),
				}));
				setOptions(treeData);
			} catch (error) {
				console.error("Failed to load categories:", error);
			} finally {
				setLoading(false);
			}
		};
		loadCategories();
	}, []);

	const handleChange = (selectedValues) => {
		const categories = (selectedValues || []).map((item) => {
			const value = typeof item === "object" ? item.value : item;
			const [id, text, isParentStr] = value.split("|");
			return { id, text, isParent: isParentStr === "true" };
		});
		onChange({ categories });
	};

	return (
		<div className="criteria-selector">
			<div className="required">
				<label className="chips-label">Add Categories</label>
			</div>
			<div className="tree-select categories">
				<TreeSelect
					showSearch
					style={{ width: "100%" }}
					value={getSelectedValues()}
					dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
					placeholder={loading ? "Loading..." : "Select categories..."}
					treeCheckable
					allowClear
					variant="borderless"
					showCheckedStrategy={TreeSelect.SHOW_PARENT}
					onChange={handleChange}
					treeData={options}
					autoClearSearchValue={false}
					disabled={isReadOnly || loading}
					labelInValue
				/>
			</div>
		</div>
	);
}

CategoriesSelector.propTypes = {
	data: PropTypes.shape({
		categories: PropTypes.array,
	}),
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
};

// Keywords selector
function KeywordsSelector({ data, onChange, isReadOnly, tagId }) {
	const [defaultOptions, setDefaultOptions] = useState([]);
	const [inputMode, setInputMode] = useState("Enter Keywords");
	const [inputValue, setInputValue] = useState("");
	const inputModes = ["Enter Keywords", "Paste Keywords"];

	const selectedKeywords = (data?.keywords || [])
		.filter((kw) => kw.trim() !== "")
		.map((kw) => ({
			label: kw,
			value: kw,
		}));

	// Build grouped options from API response (Matched + Similar)
	const buildGroupedOptions = (response) => {
		const matched = (response.data || []).map((kw) => ({ label: kw, value: kw }));
		const similar = (response.similar || []).map((kw) => ({ label: kw, value: kw }));
		const groups = [];
		if (matched.length > 0) {
			groups.push({ label: "Matched", options: matched });
		}
		if (similar.length > 0) {
			groups.push({ label: "Similar", options: similar });
		}
		return groups;
	};

	// Load initial default options
	useEffect(() => {
		const loadInitialOptions = async () => {
			if (!tagId) return;
			try {
				const response = await getKeywordsByTag(tagId, "");
				setDefaultOptions(buildGroupedOptions(response));
			} catch (error) {
				console.error("Failed to fetch keywords:", error);
			}
		};
		loadInitialOptions();
	}, [tagId]);

	// Async load options as user types
	const loadOptions = async (inputValue) => {
		if (!tagId) return [];
		try {
			const response = await getKeywordsByTag(tagId, inputValue);
			return buildGroupedOptions(response);
		} catch (error) {
			console.error("Failed to fetch keywords:", error);
			return [];
		}
	};

	const handleSelectChange = (selectedOptions, actionMeta) => {
		const keywords = (selectedOptions || []).map((item) => item.value);
		onChange({ keywords });

		// Clear input if a new keyword was created (not selected from results)
		if (actionMeta.action === "create-option") {
			setInputValue("");
		}
	};

	const handleTextareaChange = (e) => {
		const keywords = e.target.value.split("\n");
		onChange({ keywords });
	};

	return (
		<div className="criteria-selector">
			<div className="required">
				<label className="chips-label">Enter or Paste Keywords and Topics</label>
			</div>
			<div className="chips-info p5">Keywords or topics that match the content on your site</div>
			<SelectButton
				value={inputMode}
				onChange={(e) => setInputMode(e.value)}
				options={inputModes}
				className="input-mode-selector"
				unselectable={false}
			/>
			{inputMode === "Enter Keywords" ? (
				<div className="input-wrapper not-required enabled search">
					<AsyncCreatable
						name="keywords"
						classNamePrefix="select"
						className="select-field long"
						value={selectedKeywords}
						defaultOptions={defaultOptions}
						loadOptions={loadOptions}
						onChange={handleSelectChange}
						isDisabled={isReadOnly}
						isMulti={true}
						hideSelectedOptions={false}
						menuPlacement="auto"
						closeMenuOnSelect={false}
						placeholder="Type to search"
						inputValue={inputValue}
						onInputChange={(value, action) => {
							if (action.action === "input-change" || action.action === "menu-close") {
								setInputValue(value);
							}
							if (action.action === "set-value") {
								return inputValue;
							}
						}}
						formatCreateLabel={(val) => `Add "${val}"`}
						theme={(theme) => ({
							...theme,
							borderRadius: 0,
							colors: {
								...theme.colors,
								primary25: "#B3CF79",
								primary: "#9ACA3C",
							},
						})}
					/>
					<div className="input--inner"></div>
				</div>
			) : (
				<div className="textarea-wrapper">
					<div className="chips-info p5">One keyword per line</div>
					<InputTextarea
						value={(data?.keywords || []).join("\n")}
						onChange={handleTextareaChange}
						rows={10}
						placeholder={"keyword1\nkeyword2\nkeyword3"}
						disabled={isReadOnly}
					/>
				</div>
			)}
		</div>
	);
}

KeywordsSelector.propTypes = {
	data: PropTypes.shape({
		keywords: PropTypes.array,
	}),
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
	tagId: PropTypes.string,
};

// URLs selector
function UrlsSelector({ data, onChange, isReadOnly }) {
	const getSelectedCondition = () => {
		const condition = data?.urls?.[0]?.condition;
		return CONST.urlConditions.find((c) => c.value === condition) || null;
	};

	const getSelectedValues = () => {
		return data?.urls?.[0]?.values || [];
	};

	const handleConditionChange = (option) => {
		onChange({
			urls: [
				{
					condition: option.value,
					values: getSelectedValues(),
				},
			],
		});
	};

	const handleValuesChange = (e) => {
		const values = e.target.value.split("\n");
		onChange({
			urls: [
				{
					condition: getSelectedCondition()?.value || "contains",
					values,
				},
			],
		});
	};

	return (
		<div className="criteria-selector">
			<Select
				label="Criteria"
				name="url-condition"
				options={CONST.urlConditions}
				selected={getSelectedCondition()}
				onSubmit={handleConditionChange}
				disabled={isReadOnly}
				required={true}
				search={false}
			/>
			<div className="textarea-wrapper">
				<div className="required">
					<label className="chips-label">Enter or Paste URLs or URL Fragments</label>
				</div>
				<div className="chips-info p5">One entry per line</div>
				<InputTextarea
					value={getSelectedValues().join("\n")}
					onChange={handleValuesChange}
					rows={10}
					placeholder={"URL1\nURL2\nURL3"}
					disabled={isReadOnly}
				/>
			</div>
		</div>
	);
}

UrlsSelector.propTypes = {
	data: PropTypes.shape({
		urls: PropTypes.arrayOf(
			PropTypes.shape({
				condition: PropTypes.string,
				values: PropTypes.array,
			})
		),
	}),
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
};

// GARM selector
function GARMSelector({ data, onChange, isReadOnly }) {
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(true);

	const selectedGarm = (data?.garm || []).map((g) => {
		if (typeof g === "string") {
			const [id, title] = g.split("|");
			return { value: g, label: title };
		}
		return { value: `${g.id}|${g.title}`, label: g.title };
	});

	useEffect(() => {
		const loadGARM = async () => {
			try {
				setLoading(true);
				const garmData = await getGARMTaxonomy();
				const opts = garmData.map((el) => ({
					value: `${el.id}|${el.title}`,
					label: el.title,
				}));
				setOptions(opts);
			} catch (error) {
				console.error("Failed to load GARM taxonomy:", error);
			} finally {
				setLoading(false);
			}
		};
		loadGARM();
	}, []);

	const handleChange = (selectedOptions) => {
		const garm = (selectedOptions || []).map((opt) => {
			const [id, title] = opt.value.split("|");
			return { id, title };
		});
		onChange({ garm });
	};

	return (
		<div className="criteria-selector">
			<Select
				name="garm"
				label="Brand Safety Categories"
				options={options}
				selected={selectedGarm}
				onSubmit={handleChange}
				disabled={loading || isReadOnly}
				required={true}
				multi={true}
				search={true}
				placeholder={loading ? "Loading..." : "Select brand safety categories..."}
			/>
		</div>
	);
}

GARMSelector.propTypes = {
	data: PropTypes.shape({
		garm: PropTypes.array,
	}),
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
};

// Sentiments selector
function SentimentsSelector({ data, onChange, isReadOnly }) {
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(true);

	const selectedSentiments = (data?.sentiments || []).map((s) => {
		if (typeof s === "string") {
			const [id, title] = s.split("|");
			return { value: s, label: title };
		}
		return { value: `${s.id}|${s.title}`, label: s.title };
	});

	useEffect(() => {
		const loadSentiments = async () => {
			try {
				setLoading(true);
				const sentimentsData = await getSentimentTaxonomy();
				// Flatten the nested structure for Select component
				const opts = [];
				sentimentsData.forEach((el) => {
					if (el.items && el.items.length > 0) {
						el.items.forEach((item) => {
							opts.push({
								value: `${item.id}|${item.title}`,
								label: `${el.title} - ${item.title}`,
							});
						});
					} else {
						opts.push({
							value: `${el.id}|${el.title}`,
							label: el.title,
						});
					}
				});
				opts.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
				setOptions(opts);
			} catch (error) {
				console.error("Failed to load sentiments:", error);
			} finally {
				setLoading(false);
			}
		};
		loadSentiments();
	}, []);

	const handleChange = (selectedOptions) => {
		const sentiments = (selectedOptions || []).map((opt) => {
			const [id, title] = opt.value.split("|");
			return { id, title };
		});
		onChange({ sentiments });
	};

	return (
		<div className="criteria-selector">
			<Select
				name="sentiments"
				label="Sentiments"
				options={options}
				selected={selectedSentiments}
				onSubmit={handleChange}
				disabled={loading || isReadOnly}
				required={true}
				multi={true}
				search={true}
				placeholder={loading ? "Loading..." : "Select sentiments..."}
			/>
		</div>
	);
}

SentimentsSelector.propTypes = {
	data: PropTypes.shape({
		sentiments: PropTypes.array,
	}),
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
};

// Taxonomy selector
function TaxonomySelector({ data, onChange, isReadOnly, tagId }) {
	const [defaultOptions, setDefaultOptions] = useState([]);
	const [inputValue, setInputValue] = useState("");

	const exceptions = ["of", "the", "and"];
	const convertToTitleCase = (str) => {
		if (!str) return "";
		return str
			.toLowerCase()
			.split("_")
			.map((word, i) => {
				return exceptions.includes(word) && i !== 0 ? word : word.charAt(0).toUpperCase() + word.substr(1);
			})
			.join(" ");
	};

	const formatNumber = (num) => {
		const formatter = Intl.NumberFormat("en-Us", { notation: "compact" });
		return formatter.format(num);
	};

	const getSelectedValues = () => {
		if (!data?.taxonomy?.values) return [];
		return data.taxonomy.values.map((el) => ({
			label: `${convertToTitleCase(el.property)}: ${el.value}`,
			value: `${el.property}|${el.value}`,
			pageViews: el.page_views_last_30_days,
		}));
	};

	const loadOptions = async (query) => {
		if (!tagId) return [];
		try {
			const response = await getTaxonomyByTag(tagId, query);
			return response.results.map((el) => ({
				label: `${convertToTitleCase(el.property)}: ${el.value}`,
				value: `${el.property}|${el.value}`,
				pageViews: el.page_views_last_30_days,
			}));
		} catch (error) {
			console.error("Failed to load taxonomy:", error);
			return [];
		}
	};

	useEffect(() => {
		loadOptions("").then(setDefaultOptions);
	}, [tagId]);

	const handleChange = (selected) => {
		const values = (selected || []).map((el) => {
			const [property, value] = el.value.split("|");
			return { property, value, page_views_last_30_days: el.pageViews };
		});
		onChange({ taxonomy: { values } });
	};

	return (
		<div className="criteria-selector">
			<div className="required">
				<label className="chips-label">Add Criteria</label>
			</div>
			<div className="input-wrapper required enabled search">
				<AsyncSelect
					classNamePrefix="select"
					className="select-field long"
					value={getSelectedValues()}
					defaultOptions={defaultOptions}
					loadOptions={loadOptions}
					onChange={handleChange}
					isDisabled={isReadOnly}
					isMulti={true}
					hideSelectedOptions={false}
					menuPlacement="auto"
					closeMenuOnSelect={false}
					placeholder="Type to search"
					inputValue={inputValue}
					onInputChange={(value, action) => {
						if (action.action === "input-change" || action.action === "menu-close") {
							setInputValue(value);
						}
						if (action.action === "set-value") {
							return inputValue;
						}
					}}
					theme={(theme) => ({
						...theme,
						borderRadius: 0,
						colors: {
							...theme.colors,
							primary25: "#B3CF79",
							primary: "#9ACA3C",
						},
					})}
					formatOptionLabel={(opt) => (
						<div className="taxonomy-option">
							<div className="taxonomy-label">{opt.label}</div>
							<div className="taxonomy-page-views">
								<img src={eyeIcon} alt="" className="eye-icon" />
								<span>{formatNumber(opt.pageViews || 0)}</span>
							</div>
						</div>
					)}
				/>
				<div className="input--inner"></div>
			</div>
		</div>
	);
}

TaxonomySelector.propTypes = {
	data: PropTypes.shape({
		taxonomy: PropTypes.shape({
			values: PropTypes.array,
		}),
	}),
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
	tagId: PropTypes.string,
};

ContentConsumptionCard.propTypes = {
	data: PropTypes.shape({
		action: PropTypes.string,
		criteria: PropTypes.object,
		modifier: PropTypes.object,
	}),
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
	isCollapsed: PropTypes.bool,
	tagId: PropTypes.string,
	modifierConfiguration: PropTypes.array,
	modifierDefaults: PropTypes.object,
	customTaxonomies: PropTypes.array,
	cardId: PropTypes.string,
	isModifierPartiallyEditable: PropTypes.bool,
};

ContentConsumptionCard.defaultProps = {
	data: { action: "include", criteria: { type: "all" }, modifier: {} },
	isReadOnly: false,
	isCollapsed: false,
	tagId: null,
	modifierConfiguration: [],
	modifierDefaults: {},
	customTaxonomies: [],
	cardId: "0",
	isModifierPartiallyEditable: false,
};

// Export helper for getting selected count
ContentConsumptionCard.getSelectedCount = (data) => {
	const criteria = data?.criteria;
	if (!criteria) return 0;

	switch (criteria.type) {
		case "categories":
			return criteria.categories?.length || 0;
		case "keywords":
			return criteria.keywords?.length || 0;
		case "urls":
			return criteria.urls?.[0]?.values?.filter((v) => v.trim()).length || 0;
		case "garm":
			return criteria.garm?.length || 0;
		case "sentiments":
			return criteria.sentiments?.length || 0;
		case "default":
			return criteria.taxonomy?.values?.length || 0;
		case "all":
			return 1;
		default:
			return 0;
	}
};

// Helper to validate criteria data
const isValidCriteriaData = (criteria) => {
	if (!criteria || !criteria.type) return false;

	switch (criteria.type) {
		case "categories":
			return criteria.categories?.length > 0;
		case "keywords":
			return criteria.keywords?.filter((kw) => kw.trim()).length > 0;
		case "urls": {
			const urlEntry = criteria.urls?.[0];
			if (!urlEntry?.condition) return false;
			const validValues = urlEntry.values?.filter((v) => v.trim()) || [];
			return validValues.length > 0;
		}
		case "garm":
			return criteria.garm?.length > 0;
		case "sentiments":
			return criteria.sentiments?.length > 0;
		case "default":
			return criteria.taxonomy?.values?.length > 0;
		case "all":
		case null:
			return true; // No criteria filter = valid
		default:
			return false;
	}
};

// Helper to validate filterBy rules
const isValidFilterByRules = (rules) => {
	if (!rules || rules.length === 0) return true; // No rules = valid (optional)
	return rules.every((rule) => {
		const hasField = rule.field && rule.field.trim() !== "";
		const hasOperator = rule.operator && rule.operator.trim() !== "";
		let hasValue;
		if (Array.isArray(rule.value)) {
			hasValue = rule.value.some((v) => String(v).trim() !== "");
		} else {
			hasValue = rule.value !== undefined && rule.value !== null && rule.value !== "";
		}
		return hasField && hasOperator && hasValue;
	});
};

// Export helper for validating criteria data
ContentConsumptionCard.isValid = (data) => {
	const modifier = data?.modifier;
	const criteria = data?.criteria;

	// Modifier is required
	if (!modifier?.type) return false;

	// Validate modifier fields
	if (!modifier.count || !modifier.lookback) return false;
	if (modifier.type === "timeSpent" && !modifier.timeUnit) return false;

	const isCustomModifier = modifier.type.startsWith("custom|");

	if (isCustomModifier) {
		// Custom modifier: filterBy rules are optional but must be valid if provided
		const rules = modifier.filterBy?.rules || [];
		return isValidFilterByRules(rules);
	} else {
		// Standard modifier: criteria is optional but must be valid if provided
		const hasCriteria = criteria?.type && criteria.type !== "all" && criteria.type !== null;
		if (hasCriteria) {
			return isValidCriteriaData(criteria);
		}
		return true; // No criteria = valid
	}
};

export default ContentConsumptionCard;
